import { Fragment, useEffect, useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, Pencil } from 'lucide-react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Package,
  ClipboardList,
  DollarSign,
  Banknote,
  Users,
  FileBarChart,
  Key as KeyIcon,
  BookUser,
  Trophy,
  Globe,
  Star,
  Settings as SettingsIcon,
  LogOut,
  Truck,
  Archive,
  Beer,
  ListChecks,
  Inbox,
  History,
  CalendarDays,
  CalendarClock,
  User,
  BarChart3,
  HandCoins,
  Wrench,
  GraduationCap,
  FileText,
  GlassWater,
  Wine,
  CupSoda,
  Utensils,
  Download,
  ChevronDown,
  ShieldCheck,
  Clock,
  FolderOpen,
  ArrowRightLeft,
  Briefcase,
  BookOpen,
  ShoppingBag,
  Coins,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { TooltipProvider } from '@/components/ui/tooltip';
import ChatWidget from '@/features/chatbot/ChatWidget';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { useAuth } from '@/features/auth/useAuth';
import { useEmployees } from '@/features/employees/useEmployees';
import { useUsers } from '@/features/users/useUsers';
import { useRoles } from '@/features/users/useRoles';
import { useInventoryItems } from '@/features/inventory/useInventoryItems';
import {
  useSidebarLayout,
  useUpdateSidebarLayout,
} from '@/features/layout/useSidebarLayout';
import { isLowStock, isOutOfStock } from '@/features/inventory/constants';
import { usePendingSignatures } from '@/features/formation/usePendingSignatures';
import { useMyPendingHrAssignments } from '@/features/hr-documents/useHrAssignments';
import { useNewApplicationsCount } from '@/features/jobs/useJobs';
import { useUnreadSms, SMS_UNREAD_KEY } from '@/features/sms/useUnreadSms';
import {
  usePendingOrdersCount,
  BOUTIQUE_PENDING_KEY,
} from '@/features/boutique/useBoutique';
import {
  useOpenReportMap,
  MENU_REPORTS_MAP_KEY,
  MENU_REPORTS_KEY,
} from '@/features/menuReports/useMenuReports';
import { ROLE_LABELS, isAdminRole } from '@/types/userProfile';
import { supabase } from '@/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Logo from '@/components/Logo';

// Chaque item est associé soit à un sectionId (visible si au moins UNE
// sous-permission de la section est accordée) soit à un permissionId
// précis (visible uniquement si cette perm exacte est accordée).
type NavItem = {
  /** ID stable utilisé pour persister l'ordre custom dans sidebar_layout. */
  id: string;
  label: string;
  path: string;
  icon: typeof Package;
  sectionId?: string;
  permissionId?: string;
  /** Si l'user a CETTE perm, on cache l'item (ex: cacher "Ma formation"
   * aux formateurs qui voient déjà "Formation"). */
  hideIfPermission?: string;
  /** Si true, visible pour tout user authentifié ayant un employee_id
   * lié (peu importe les permissions). Pour les pages self-service. */
  alwaysIfEmployee?: boolean;
  /** Si true, visible uniquement pour rôle owner/admin (court-circuite
   * le système de permissions granulaires). */
  requiresAdmin?: boolean;
  /** Si true, visible pour TOUT utilisateur authentifié — peu importe son
   * rôle, employee_id ou permissions. Ex: page Recettes en mode service. */
  alwaysAuthenticated?: boolean;
};

const navItems: NavItem[] = [
  { id: 'inventory',     label: 'Inventaire',          path: '/inventory',       icon: Package,       sectionId: 'inventory' },
  { id: 'prix-vente',    label: 'Prix de vente',       path: '/prix-vente',      icon: DollarSign,    sectionId: 'prix-vente',  requiresAdmin: true },
  { id: 'shifts',        label: 'Quarts',              path: '/shifts',          icon: ClipboardList, sectionId: 'shifts' },
  { id: 'schedule',      label: 'Horaire',             path: '/horaire',         icon: CalendarClock, sectionId: 'schedule' },
  { id: 'payroll-tips',  label: 'Gestion des paies',   path: '/payroll/tips',    icon: DollarSign,    sectionId: 'payroll-tips' },
  // « Entrée de temps » (/payroll/salary) a migré sous Ressources Humaines
  // (voir hrSubItems). Plus d'item top-level dédié.
  { id: 'hr',            label: 'Ressources Humaines', path: '/employees',       icon: Users,         sectionId: 'hr' },
  { id: 'admin-events',  label: 'Événements',          path: '/admin/events',    icon: Trophy,        sectionId: 'events',      requiresAdmin: true },
  { id: 'admin-publications', label: 'Publications site', path: '/admin/publications', icon: Globe,      sectionId: 'publications', requiresAdmin: true },
  { id: 'admin-ratings',  label: 'Avis & notations',   path: '/admin/avis',         icon: Star,        sectionId: 'ratings',      requiresAdmin: true },
  { id: 'admin-reservations', label: 'Réservations',   path: '/admin/reservations', icon: CalendarDays, sectionId: 'reservations', requiresAdmin: true },
  { id: 'cluster-import', label: 'Import Cluster',  path: '/admin/cluster-import', icon: Download,    permissionId: 'cluster.import', requiresAdmin: true },
  { id: 'audit-rapports', label: 'Audit rapports (IA)', path: '/admin/audit-rapports', icon: ClipboardList, requiresAdmin: true },
  { id: 'suivi-corrections', label: 'Suivi corrections', path: '/admin/suivi-corrections', icon: ClipboardList, requiresAdmin: true },
  // Recettes — mode service derrière le bar. Désormais délégable via la
  // section « recipes » (les gabarits de tous les rôles l'incluent par
  // défaut, donc l'accès universel est préservé tout en restant retirable
  // par personne). requiresAdmin garde owner/admin toujours visibles.
  { id: 'recettes',      label: 'Recettes',            path: '/recettes',        icon: GlassWater,    sectionId: 'recipes',     requiresAdmin: true },
  // Mes documents HR — visible pour tout employé. Cachée pour les
  // gérants qui voient déjà la bibliothèque dans Ressources Humaines.
  { id: 'mes-documents', label: 'Mes documents',       path: '/mes-documents',   icon: FileText,      alwaysIfEmployee: true,        hideIfPermission: 'hr.documents' },
  // Guide d'utilisation — visible pour tout employé (page self-service).
  { id: 'aide',          label: "Guide d'utilisation", path: '/aide',            icon: BookOpen,      alwaysIfEmployee: true },
  // Boutique de linge à points — visible pour tout employé.
  { id: 'boutique',      label: 'Boutique',            path: '/boutique',        icon: ShoppingBag,   alwaysIfEmployee: true },
  // "Paramètres" en dernier, juste avant le bloc user en bas de la sidebar.
  // Visible si l'user a au moins une perm de paramètre (pour l'instant : hr.roles).
  // Paramètres : visible si l'user a hr.roles (rôles) OU toute perm
  // settings.* (catégories d'inventaire, addons/formats cocktails).
  { id: 'settings',      label: 'Paramètres',          path: '/settings',        icon: SettingsIcon,  sectionId: 'settings',   permissionId: 'hr.roles' },
];

// Sous-items affichés sous "Inventaire" quand on est sur /inventory/*
type SubNavItem = {
  label: string;
  path: string;
  icon: typeof Package;
  /** Permission requise. Si absent, requiresAdmin doit être à true. */
  permissionId?: string;
  /** Visible uniquement pour owner/admin (court-circuite permissionId). */
  requiresAdmin?: boolean;
  /** Si true, le NavLink ne match QUE le path exact (pas les enfants) */
  exact?: boolean;
  /** Si défini, le sub-item match aussi sur ?tab=. Le path doit alors inclure
   *  le query (ex: '/shifts?tab=kpi'). Utile pour exposer un onglet d'une
   *  autre page comme un sub-item de menu (ex: KPI affichée sous Gestion
   *  des paies bien que la route vive sous /shifts). */
  tab?: string;
};
const inventorySubItems: SubNavItem[] = [
  { label: 'Inventaire',  path: '/inventory',           icon: ListChecks,    permissionId: 'inventory.view',     exact: true },
  { label: 'Commandes',   path: '/inventory/orders',    icon: ClipboardList, permissionId: 'inventory.orders' },
  { label: 'Fournisseurs', path: '/inventory/suppliers', icon: Truck,        permissionId: 'inventory.suppliers' },
  { label: 'Archives',    path: '/inventory/archives',  icon: Archive,       permissionId: 'inventory.archives' },
  { label: 'Fûts',        path: '/inventory/kegs',      icon: Beer,          permissionId: 'inventory.kegs' },
  { label: 'Commandes en attente de réception', path: '/inventory/receive', icon: Inbox, permissionId: 'inventory.receive' },
  { label: 'Historique',  path: '/inventory/history',   icon: History,       permissionId: 'inventory.history' },
];

// Sous-items affichés sous "Prix de vente". Chaque sub-item est lié à
// une sous-permission précise + ouvert à admin/owner par défaut.
const prixVenteSubItems: SubNavItem[] = [
  { label: 'Spiritueux',   path: '/prix-vente',                icon: DollarSign, permissionId: 'prix-vente.bouteilles', requiresAdmin: true, exact: true },
  { label: 'Whisky',       path: '/prix-vente/whisky',         icon: GlassWater, permissionId: 'prix-vente.whisky',     requiresAdmin: true },
  { label: 'Vins',         path: '/prix-vente/vins',           icon: Wine,       permissionId: 'prix-vente.vins',       requiresAdmin: true },
  { label: 'Canettes',     path: '/prix-vente/canettes',       icon: CupSoda,    permissionId: 'prix-vente.canettes',   requiresAdmin: true },
  { label: 'Fûts',         path: '/prix-vente/futs',           icon: Beer,       permissionId: 'prix-vente.futs',       requiresAdmin: true },
  { label: 'Cocktails',    path: '/prix-vente/cocktails',      icon: GlassWater, permissionId: 'prix-vente.cocktails',  requiresAdmin: true },
  { label: 'Bouffe',       path: '/prix-vente/bouffe',         icon: Utensils,   permissionId: 'prix-vente.bouffe',     requiresAdmin: true },
  { label: 'Extras',       path: '/prix-vente/extras',         icon: Utensils,   permissionId: 'prix-vente.bouffe',     requiresAdmin: true },
  { label: 'Préparations', path: '/prix-vente/preparations',   icon: Wrench,     permissionId: 'prix-vente.bouffe',     requiresAdmin: true },
];

const payrollSubItems: SubNavItem[] = [
  { label: 'Rapport Employeur D', path: '/payroll/report',           icon: FileBarChart, permissionId: 'payroll-report.view' },
  { label: 'Grille horaire',      path: '/payroll/tips',             icon: CalendarDays, permissionId: 'payroll-tips.grid', exact: true },
  { label: 'Remises Interac',     path: '/payroll/tips/payouts',     icon: HandCoins,    permissionId: 'payroll-tips.payouts' },
  { label: 'Régler les écarts',   path: '/payroll/tips/corrections', icon: Wrench,       permissionId: 'payroll-tips.corrections' },
  // Donnée comptable et Écarts détectés : la route vit sous /shifts?tab=…
  // mais on les expose ici parce qu'elles concernent la paie au quotidien.
  { label: 'Donnée comptable',    path: '/shifts?tab=kpi',           icon: BarChart3,    permissionId: 'shifts.kpi',         tab: 'kpi' },
  { label: 'Écarts détectés',     path: '/shifts?tab=adjustments',   icon: Wrench,       permissionId: 'shifts.adjustments', tab: 'adjustments' },
];

// Sous-items pour Ressources Humaines : la page /employees a 3 onglets
// internes (Équipe / Bibliothèque / Paquets) exposés via ?tab=, plus
// le Suivi des clés qui vit sur sa propre route /keys.
//
// `tab` :
//   - string  → match si ?tab=<valeur> sur /employees
//   - null    → match si /employees sans tab (onglet « Équipe »)
//   - undefined → item externe (path complet), match sur pathname seul
type HrSubItem = {
  label: string;
  path: string;
  tab: string | null | undefined;
  icon: typeof Package;
  /** Permission requise. Si absent, requiresAdmin doit être true. */
  permissionId?: string;
  /** Item admin/owner only (court-circuite permissionId). */
  requiresAdmin?: boolean;
  /** Si l'user a CETTE perm, on cache l'item (ex: cacher 'Ma formation'
   *  aux formateurs qui voient déjà 'Formation'). */
  hideIfPermission?: string;
};
const hrSubItems: HrSubItem[] = [
  { label: 'Équipe',         path: '/employees',                tab: null,        icon: Users,        permissionId: 'hr.team' },
  { label: 'Entrée de temps', path: '/payroll/salary',          tab: undefined,   icon: Banknote,     permissionId: 'payroll-salary.view' },
  { label: 'Bibliothèque',   path: '/employees?tab=documents',  tab: 'documents', icon: FileText,     permissionId: 'hr.documents' },
  { label: 'Paquets',        path: '/employees?tab=packs',      tab: 'packs',     icon: Package,      permissionId: 'hr.documents' },
  { label: 'Formation',      path: '/formation',                tab: undefined,   icon: GraduationCap, permissionId: 'formation.train' },
  { label: 'Ma formation',   path: '/ma-formation',             tab: undefined,   icon: GraduationCap, permissionId: 'formation.self', hideIfPermission: 'formation.train' },
  { label: 'Répertoire',     path: '/repertoire',               tab: undefined,   icon: BookUser,     permissionId: 'directory.view' },
  { label: 'Suivi des clés', path: '/keys',                     tab: undefined,   icon: KeyIcon,      permissionId: 'keys.view' },
  { label: 'Emplois',        path: '/admin/emplois',            tab: undefined,   icon: Briefcase,    permissionId: 'jobs.view' },
];

// Sous-items pour Recettes (mode service derrière le bar).
// Tous accessibles à tout staff authentifié, donc pas de permissionId.
const recettesSubItems: SubNavItem[] = [
  { label: 'Cocktails', path: '/recettes',        icon: GlassWater, permissionId: 'recipes.cocktails', requiresAdmin: true, exact: true },
  { label: 'Bouffe',    path: '/recettes/bouffe', icon: Utensils,   permissionId: 'recipes.food',      requiresAdmin: true },
];

// Sous-items pour « Horaire ». Employé : Mon horaire, Horaire de l'équipe
// (lecture seule) + Mes disponibilités.
// Planificateur : Calendrier, Quarts à combler, Disponibilités, Échanges, Équipe.
const scheduleSubItems: SubNavItem[] = [
  { label: 'Mon horaire',        path: '/horaire/mon-horaire',       icon: CalendarClock, permissionId: 'schedule.view_own' },
  { label: "Horaire de l'équipe", path: '/horaire/horaire-equipe',   icon: Users,         permissionId: 'schedule.view_team' },
  { label: 'Mes disponibilités', path: '/horaire/mes-disponibilites', icon: CalendarDays,  permissionId: 'schedule.availability' },
  { label: 'Calendrier',       path: '/horaire',                icon: CalendarClock, permissionId: 'schedule.manage', exact: true },
  { label: 'Quarts à combler', path: '/horaire/a-combler',      icon: Inbox,         permissionId: 'schedule.manage' },
  { label: 'Disponibilités',   path: '/horaire/disponibilites', icon: CalendarDays,  permissionId: 'schedule.manage' },
  { label: 'Échanges',         path: '/horaire/echanges',       icon: ArrowRightLeft, permissionId: 'schedule.manage' },
  { label: 'Équipe',           path: '/horaire/equipe',         icon: Star,          permissionId: 'schedule.manage' },
];

// Sous-sous-items affichés sous « Équipe » quand un employé est sélectionné.
// Mirror de EMPLOYEE_SECTIONS dans pages/Employees.tsx — la sidebar pousse
// dans ?section= ce que le drilldown master-detail de la page accepte.
type EmployeeSectionItem = {
  id: string;
  label: string;
  icon: typeof Package;
};
const employeeSections: EmployeeSectionItem[] = [
  { id: 'info',        label: 'Infos',                icon: User },
  { id: 'access',      label: 'Accès & compte',       icon: ShieldCheck },
  { id: 'hours',       label: 'Heures & pourboires',  icon: Clock },
  { id: 'points',      label: 'Points & Boutique',    icon: Coins },
  { id: 'adjustments', label: 'Ajustements',          icon: DollarSign },
  { id: 'documents',   label: 'Documents',            icon: FolderOpen },
  { id: 'delegation',  label: 'Délégation',           icon: ArrowRightLeft },
];

// Sous-items pour Quarts : KPI et Ajustements ont migré sous Gestion des paies.
const shiftsSubItems: HrSubItem[] = [
  { label: 'Rapport du jour', path: '/shifts',                 tab: null,         icon: CalendarDays,  permissionId: 'shifts.daily' },
  { label: 'Historique',      path: '/shifts?tab=history',     tab: 'history',    icon: History,       permissionId: 'shifts.history' },
  { label: 'Paramètres',      path: '/shifts?tab=settings',    tab: 'settings',   icon: SettingsIcon,  permissionId: 'shifts.settings' },
];

// Tabs Quarts qui ont migré sous Gestion des paies — utilisé pour empêcher
// l'item « Quarts » d'être marqué actif quand l'utilisateur est sur ces onglets.
const SHIFTS_TABS_MIGRATED_TO_PAYROLL = new Set<string>(['kpi', 'adjustments']);

/**
 * Calcule un libellé de section pour le header sticky à droite du
 * SidebarTrigger. Affiche le label le plus profond qui correspond à
 * l'URL courante (pathname + query params).
 *
 * Précédence (du plus précis au moins précis) :
 *   1. Section d'un employé (?employee=…&section=…) → "Heures & pourboires"
 *   2. Sous-onglet Quarts ou HR (?tab=…)             → "Historique", "Bibliothèque"
 *   3. Sub-item path-based (Inventaire, Paie, …)     → "Commandes", "Sommaire"
 *   4. Item principal                                → "Quarts", "Recettes"
 */
function getCurrentPageLabel(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  const tab = params.get('tab');

  // Fiche employé avec section : on est plus précis que l'onglet « Équipe »
  if (pathname.startsWith('/employees') && params.get('employee')) {
    const sectionId = params.get('section') ?? 'info';
    const sec = employeeSections.find((s) => s.id === sectionId);
    if (sec) return sec.label;
  }

  // Sous-onglets Quarts (?tab=)
  if (pathname.startsWith('/shifts')) {
    const sub = shiftsSubItems.find((s) => s.tab === tab);
    if (sub) return sub.label;
  }

  // Sous-onglets HR (?tab=)
  if (pathname.startsWith('/employees')) {
    const sub = hrSubItems.find((s) => s.tab === tab);
    if (sub) return sub.label;
  }

  // Sub-items basés sur le pathname (Inventaire, Paie tips, Prix de vente).
  // Certains sub-items utilisent un query ?tab= (ex: KPI sous Gestion des
  // paies pointe vers /shifts?tab=kpi) → matching combiné (basePath, tab).
  const allSubs = [...inventorySubItems, ...payrollSubItems, ...prixVenteSubItems, ...recettesSubItems, ...scheduleSubItems];
  const sub = allSubs.find((s) => {
    if (s.tab !== undefined) {
      const basePath = s.path.split('?')[0];
      return pathname.startsWith(basePath) && tab === s.tab;
    }
    return s.exact ? pathname === s.path : pathname.startsWith(s.path);
  });
  if (sub) return sub.label;

  // Suivi des clés vit sous /keys mais a été déplacé sous RH dans la nav.
  if (pathname.startsWith('/keys')) return 'Suivi des clés';

  // Sinon, fallback sur les items principaux
  const main = navItems.find((i) => pathname.startsWith(i.path));
  if (main) return main.label;

  return 'Bar L\'Alambic';
}

/**
 * Sidebar applicative — wrapping de la primitive shadcn `Sidebar`
 * avec la logique métier Baralambic (permissions, badges, sub-items
 * dynamiques selon route, dropdown commandes par responsable).
 */
function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, employee, profile, hasSectionAccess, hasPermission, isAdminOrOwner, signOut } = useAuth();

  // Override d'expansion par item :
  //   undefined = comportement par défaut (= ouvert si on est sur la page)
  //   true      = force-ouvert (même quand on n'est pas sur la page)
  //   false     = force-fermé (même quand on est sur la page)
  //
  // Les overrides sont remis à zéro pour un item dès qu'on navigue VERS
  // sa page parente (cf. useEffect ci-dessous), pour que le sub-menu
  // suive le comportement par défaut quand on revient sur sa page.
  // Sans ce reset, fermer Inventaire puis revenir dessus le laisserait
  // fermé indéfiniment, et fermer Inventaire puis aller ailleurs le
  // laisserait apparaître ouvert sur l'autre page (= bug bizarre).
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map());

  // Pliage manuel de la liste d'employés sous « Équipe ». false = ouvert
  // (défaut), true = replié. Persiste tant que l'app est ouverte.
  const [teamListCollapsed, setTeamListCollapsed] = useState(false);

  // Catégories de rôle dépliées sous « Équipe ». Par défaut toutes les
  // catégories sont fermées : l'utilisateur ouvre seulement celles dont il
  // a besoin. On stocke les ids dépliés. Persiste tant que l'app est ouverte.
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    () => new Set()
  );
  const toggleCategory = (id: string) =>
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Afficher (ou non) les anciens employés — status 'inactive' — dans la
  // liste « Équipe ». Masqués par défaut pour garder la liste légère.
  const [showInactive, setShowInactive] = useState(false);

  const { pathname } = location;
  useEffect(() => {
    setOverrides((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const path of next.keys()) {
        if (pathname.startsWith(path)) {
          next.delete(path);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pathname]);

  const toggleExpanded = (path: string, currentlyOpen: boolean) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(path, !currentlyOpen);
      return next;
    });
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const email = user?.email ?? 'Utilisateur';
  const displayName = employee
    ? `${employee.first_name}${employee.last_name ? ' ' + employee.last_name : ''}`.trim()
    : email;
  const roleLabel = profile?.role
    ? ROLE_LABELS[profile.role]
    : 'Compte sans profil';

  // ─── Signatures de formation en attente pour MOI ─────────────
  const { data: pendingSigs = [] } = usePendingSignatures();
  const pendingCount = pendingSigs.length;

  // ─── Documents HR à remplir/signer (assignations pending/in_progress)
  const { data: pendingDocs = [] } = useMyPendingHrAssignments();
  const pendingDocsCount = pendingDocs.length;

  // ─── Candidatures emplois non vues (status='new')
  // Visible seulement aux admin/owner via le path /admin/emplois — la
  // query passe la RLS donc anyway count = 0 pour les non-admins.
  const { data: newJobAppsCount = 0 } = useNewApplicationsCount();

  // ─── SMS entrants non lus (badge Répertoire + toast temps réel) ──────
  const unreadSms = useUnreadSms().total;
  const canSeeDirectory = isAdminOrOwner || hasPermission('directory.view');
  const queryClient = useQueryClient();

  // ─── Commandes boutique en attente (badge + toast) ──────────
  const pendingOrders = usePendingOrdersCount();

  // ─── Signalements menu ouverts (badge + toast) ──────────────
  const openReports = useOpenReportMap().total;

  // ─── Employés (pour le drilldown sous « Équipe ») ─────────────
  // On ne fetch que si l'user a accès à hr.team — sinon le sub-item
  // « Équipe » n'apparaît pas dans la sidebar.
  const canSeeTeam = hasPermission('hr.team');
  const { data: allEmployees = [] } = useEmployees({ enabled: canSeeTeam });
  const employeeIdParam = new URLSearchParams(location.search).get('employee');
  const visibleEmployees = useMemo(() => {
    if (!canSeeTeam) return [];
    // Actifs par défaut + l'employé courant s'il n'est pas actif (pour
    // pas qu'il disparaisse de la sidebar quand on consulte sa fiche).
    // Les inactifs ('inactive') n'apparaissent que si « Voir les anciens »
    // est activé (ils sont alors regroupés dans une catégorie dédiée).
    return allEmployees.filter(
      (emp) =>
        emp.status === 'active' ||
        emp.id === employeeIdParam ||
        (showInactive && emp.status === 'inactive')
    );
  }, [allEmployees, employeeIdParam, canSeeTeam, showInactive]);

  // Nombre d'anciens employés (inactifs) — pour afficher le bouton bascule.
  const inactiveCount = useMemo(
    () => allEmployees.filter((emp) => emp.status === 'inactive').length,
    [allEmployees]
  );

  // SMS entrants en temps réel : met à jour les badges + toast à l'arrivée.
  useEffect(() => {
    if (!canSeeDirectory) return;
    const channel = supabase
      .channel('sms-global')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sms_messages' },
        (payload) => {
          void queryClient.invalidateQueries({ queryKey: SMS_UNREAD_KEY });
          if (payload.eventType === 'INSERT') {
            const row = payload.new as {
              direction: string;
              employee_id: string | null;
              body: string;
            };
            if (row.direction === 'in') {
              const emp = allEmployees.find((e) => e.id === row.employee_id);
              const who = emp ? emp.first_name : 'un employé';
              toast.message(`💬 Nouveau SMS — ${who}`, {
                description: row.body.slice(0, 80),
                action: {
                  label: 'Voir',
                  onClick: () => navigate('/repertoire'),
                },
              });
            }
          }
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [canSeeDirectory, queryClient, allEmployees, navigate]);

  // Nouvelles commandes boutique en temps réel : badge + toast.
  useEffect(() => {
    if (!isAdminOrOwner) return;
    const channel = supabase
      .channel('boutique-orders')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'clothing_orders' },
        (payload) => {
          void queryClient.invalidateQueries({ queryKey: BOUTIQUE_PENDING_KEY });
          void queryClient.invalidateQueries({ queryKey: ['boutique', 'orders'] });
          if (payload.eventType === 'INSERT') {
            const row = payload.new as { employee_id: string | null };
            const emp = allEmployees.find((e) => e.id === row.employee_id);
            const who = emp ? emp.first_name : 'un employé';
            toast.message(`🛍️ Nouvelle commande boutique — ${who}`, {
              action: {
                label: 'Voir',
                onClick: () => navigate('/settings/boutique'),
              },
            });
          }
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [isAdminOrOwner, queryClient, allEmployees, navigate]);

  // Signalements sur le menu (cocktail/bouffe) en temps réel : badge + toast.
  useEffect(() => {
    if (!isAdminOrOwner) return;
    const channel = supabase
      .channel('menu-reports')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'menu_item_reports' },
        (payload) => {
          void queryClient.invalidateQueries({ queryKey: MENU_REPORTS_MAP_KEY });
          void queryClient.invalidateQueries({ queryKey: MENU_REPORTS_KEY });
          if (payload.eventType === 'INSERT') {
            const row = payload.new as {
              item_type: string;
              item_name: string;
              employee_id: string | null;
            };
            const emp = allEmployees.find((e) => e.id === row.employee_id);
            const who = emp ? emp.first_name : 'un employé';
            toast.message(`🚩 Signalement menu — ${row.item_name}`, {
              description: `de ${who}`,
              action: {
                label: 'Voir',
                onClick: () =>
                  navigate(
                    row.item_type === 'food'
                      ? '/prix-vente/bouffe'
                      : '/prix-vente/cocktails'
                  ),
              },
            });
          }
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [isAdminOrOwner, queryClient, allEmployees, navigate]);

  // ─── Regroupement des employés par rôle de compte ─────────────
  // La catégorie d'un employé = user_profiles.role (lié via employee_id).
  // Les libellés + l'ordre des catégories suivent role_templates (mêmes
  // que le menu déroulant de la fiche employé) ; fallback sur ROLE_LABELS.
  // Les employés sans compte tombent dans la catégorie « Autres ».
  const { data: usersList = [] } = useUsers({ enabled: canSeeTeam });
  const { data: dynamicRoles = [] } = useRoles();

  const employeeRoleMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of usersList) {
      if (u.profile.employee_id) {
        map.set(u.profile.employee_id, u.profile.role);
      }
    }
    return map;
  }, [usersList]);

  const NONE_CATEGORY = '__none__';
  const INACTIVE_CATEGORY = '__inactive__';
  const employeeCategories = useMemo(() => {
    const cats =
      dynamicRoles.length > 0
        ? dynamicRoles.map((r) => ({ id: r.role, label: r.label }))
        : (Object.keys(ROLE_LABELS) as Array<keyof typeof ROLE_LABELS>).map(
            (r) => ({ id: r as string, label: ROLE_LABELS[r] })
          );
    // « Anciens / inactifs » toujours en dernier.
    return [
      ...cats,
      { id: NONE_CATEGORY, label: 'Autres' },
      { id: INACTIVE_CATEGORY, label: 'Anciens / inactifs' },
    ];
  }, [dynamicRoles]);

  // Renvoie [{ id, label, employees }] pour les catégories non vides,
  // dans l'ordre de `employeeCategories`. L'ordre interne reste alpha
  // (visibleEmployees est déjà trié par prénom).
  const groupedEmployees = useMemo(() => {
    const buckets = new Map<string, typeof visibleEmployees>();
    for (const emp of visibleEmployees) {
      let key: string;
      if (emp.status === 'inactive') {
        // Les inactifs vont tous dans la catégorie « Anciens », peu importe
        // leur rôle, pour être faciles à retrouver et à réactiver.
        key = INACTIVE_CATEGORY;
      } else {
        const role = employeeRoleMap.get(emp.id) ?? NONE_CATEGORY;
        const known = employeeCategories.some((c) => c.id === role);
        key = known ? role : NONE_CATEGORY;
      }
      const list = buckets.get(key) ?? [];
      list.push(emp);
      buckets.set(key, list);
    }
    return employeeCategories
      .map((c) => ({ ...c, employees: buckets.get(c.id) ?? [] }))
      .filter((c) => c.employees.length > 0);
  }, [visibleEmployees, employeeRoleMap, employeeCategories]);

  // ─── Responsables avec commandes à passer (mini-dropdown sous "Commandes") ─
  const canSeeOrders = hasPermission('inventory.orders');
  const { data: items = [] } = useInventoryItems();
  const ordersByResp = useMemo(() => {
    if (!canSeeOrders) return [] as Array<[string, number]>;
    const map = new Map<string, number>();
    for (const it of items) {
      const needsOrder =
        (it.status === 'Actif' && (isLowStock(it) || isOutOfStock(it))) ||
        it.status === 'En attente' ||
        it.status === 'B/O';
      if (!needsOrder) continue;
      if (!it.responsible) continue;
      const fullName = `${it.responsible.first_name}${
        it.responsible.last_name ? ' ' + it.responsible.last_name : ''
      }`.trim();
      if (!fullName) continue;
      map.set(fullName, (map.get(fullName) ?? 0) + 1);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [canSeeOrders, items]);

  const visibleItems = navItems.filter((item) => {
    if (item.hideIfPermission && hasPermission(item.hideIfPermission)) return false;
    if (item.alwaysAuthenticated) return !!user;
    if (item.alwaysIfEmployee) return !!employee;
    // RH est un méta-item : un sub-item externe (Suivi des clés) vit sous
    // la section « keys ». On affiche RH si l'user a au moins une perm
    // de l'une des sections regroupées.
    if (item.id === 'hr') {
      return (
        hasSectionAccess('hr') ||
        hasSectionAccess('keys') ||
        hasSectionAccess('payroll-salary') ||
        hasSectionAccess('formation') ||
        hasSectionAccess('directory') ||
        hasSectionAccess('jobs')
      );
    }
    // Logique OR : un item est visible si N'IMPORTE LAQUELLE des
    // conditions définies passe (admin OU permission précise OU section).
    // Permet de combiner « toujours visible aux admins » + « délégueable
    // à un gérant via section_access ».
    const isAdmin = isAdminRole(profile?.role);
    if (item.requiresAdmin && isAdmin) return true;
    if (item.permissionId && hasPermission(item.permissionId)) return true;
    if (item.sectionId && hasSectionAccess(item.sectionId)) return true;
    return false;
  });

  // ─── Ordre custom de la sidebar (top-level) ─────────────────
  // Le proprio peut réorganiser via le mode édition. L'ordre est partagé
  // par TOUS les utilisateurs (la table sidebar_layout est un singleton).
  const isOwner = isAdminRole(profile?.role);
  const { data: savedOrder = [] } = useSidebarLayout();
  const updateLayoutMutation = useUpdateSidebarLayout();
  const [isEditingLayout, setIsEditingLayout] = useState(false);

  const orderedItems = useMemo<NavItem[]>(() => {
    if (savedOrder.length === 0) return visibleItems;
    const byId = new Map(visibleItems.map((i) => [i.id, i]));
    const result: NavItem[] = [];
    for (const id of savedOrder) {
      const item = byId.get(id);
      if (item) {
        result.push(item);
        byId.delete(id);
      }
    }
    // Items du code pas encore dans l'ordre sauvegardé → en fin de liste
    for (const item of byId.values()) result.push(item);
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(visibleItems.map((i) => i.id)), savedOrder]);

  const moveItem = (fromIndex: number, direction: -1 | 1) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= orderedItems.length) return;
    const reordered = [...orderedItems];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    updateLayoutMutation.mutate(reordered.map((i) => i.id));
  };

  return (
    <Sidebar collapsible="icon">
      {/* ─── Header : logo + titre cliquables vers le site public ─── */}
      <SidebarHeader className="border-b border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              size="lg"
              tooltip="Retour au site public"
              className="data-[active=true]:bg-transparent"
            >
              <Link to="/">
                <Logo size="h-10 shrink-0" />
                <div className="grid flex-1 text-left leading-tight">
                  <span className="font-display text-base font-bold text-primary truncate">
                    Bar L'Alambic
                  </span>
                  <span className="text-[10px] text-muted-foreground tracking-wide uppercase">
                    Système de gestion
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* ─── Navigation principale ─── */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Bouton « Réorganiser » — visible uniquement pour owner/admin */}
              {isOwner && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => setIsEditingLayout((v) => !v)}
                    tooltip={
                      isEditingLayout
                        ? 'Terminer la réorganisation'
                        : 'Réorganiser le menu'
                    }
                    className={
                      isEditingLayout
                        ? 'bg-amber-500/15 text-amber-400'
                        : 'text-neutral-400'
                    }
                  >
                    <Pencil />
                    <span>
                      {isEditingLayout ? 'Terminer' : 'Réorganiser le menu'}
                    </span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {orderedItems.map((item, itemIndex) => {
                const Icon = item.icon;
                const currentTab = new URLSearchParams(location.search).get('tab');

                const subItemsForParent =
                  item.path === '/inventory' ? inventorySubItems
                  : item.path === '/payroll/tips' ? payrollSubItems
                  : item.path === '/prix-vente' ? prixVenteSubItems
                  : item.path === '/recettes' ? recettesSubItems
                  : item.path === '/horaire' ? scheduleSubItems
                  : null;

                // Item considéré actif si on est sur sa page parent OU sur
                // un de ses sous-items. Sub-items basés sur ?tab= matchent
                // sur le couple (basePath, currentTab).
                //
                // Cas spécial Quarts : KPI et Ajustements ont migré sous
                // Gestion des paies. Quarts ne doit donc pas être marqué
                // actif quand l'utilisateur est sur l'un de ces tabs.
                const isShiftsMigratedTab =
                  item.path === '/shifts' &&
                  currentTab !== null &&
                  SHIFTS_TABS_MIGRATED_TO_PAYROLL.has(currentTab);

                // RH absorbe : Suivi des clés, Formation, Ma formation,
                // Répertoire. RH reste actif/ouvert quand on est sur ces routes.
                const isHrAbsorbingExternal =
                  item.id === 'hr' &&
                  (location.pathname.startsWith('/keys') ||
                    location.pathname.startsWith('/formation') ||
                    location.pathname.startsWith('/ma-formation') ||
                    location.pathname.startsWith('/repertoire') ||
                    location.pathname.startsWith('/payroll/salary'));

                const isActive =
                  !isShiftsMigratedTab &&
                  (location.pathname.startsWith(item.path) ||
                    isHrAbsorbingExternal ||
                    (subItemsForParent ?? []).some((s) => {
                      if (s.tab !== undefined) {
                        const basePath = s.path.split('?')[0];
                        return (
                          location.pathname.startsWith(basePath) &&
                          currentTab === s.tab
                        );
                      }
                      return s.exact
                        ? location.pathname === s.path
                        : location.pathname.startsWith(s.path);
                    }));

                const hasHrSubs = item.path === '/employees';
                const hasShiftsSubs = item.path === '/shifts';
                const hasAnySubs =
                  !!subItemsForParent || hasHrSubs || hasShiftsSubs;

                // Override prend le pas sur le défaut (= isActive). undefined
                // = pas d'override → on suit isActive.
                const override = overrides.get(item.path);
                const isOpen =
                  override !== undefined ? override : isActive;

                const showSubItems = !!subItemsForParent && isOpen;
                const showHrSubItems = hasHrSubs && isOpen;
                const showShiftsSubItems = hasShiftsSubs && isOpen;

                // Badge rouge selon l'item. Formation/Ma formation sont
                // maintenant sous RH (/employees), donc le badge des
                // signatures en attente migre sur le parent RH. Emplois est
                // maintenant sous-item RH aussi, on cumule les candidatures.
                let badgeCount = 0;
                let badgeTitle = '';
                if (item.path === '/employees') {
                  const hrTotal = pendingCount + newJobAppsCount + unreadSms;
                  if (hrTotal > 0) {
                    badgeCount = hrTotal;
                    const parts: string[] = [];
                    if (pendingCount > 0)
                      parts.push(
                        `${pendingCount} signature${pendingCount > 1 ? 's' : ''} de formation`,
                      );
                    if (newJobAppsCount > 0)
                      parts.push(
                        `${newJobAppsCount} candidature${newJobAppsCount > 1 ? 's' : ''} emploi`,
                      );
                    if (unreadSms > 0)
                      parts.push(
                        `${unreadSms} SMS non lu${unreadSms > 1 ? 's' : ''}`,
                      );
                    badgeTitle = parts.join(' · ');
                  }
                } else if (item.path === '/mes-documents' && pendingDocsCount > 0) {
                  badgeCount = pendingDocsCount;
                  badgeTitle = `${pendingDocsCount} document${pendingDocsCount > 1 ? 's' : ''} à remplir`;
                } else if (item.path === '/settings' && pendingOrders > 0) {
                  badgeCount = pendingOrders;
                  badgeTitle = `${pendingOrders} commande${pendingOrders > 1 ? 's' : ''} boutique en attente`;
                } else if (item.path === '/prix-vente' && openReports > 0) {
                  badgeCount = openReports;
                  badgeTitle = `${openReports} signalement${openReports > 1 ? 's' : ''} sur le menu`;
                }
                const showPendingBadge = badgeCount > 0;

                return (
                  <Fragment key={item.path}>
                    <SidebarMenuItem>
                      {hasAnySubs ? (
                        // Item parent avec sous-items : pas de navigation. Le
                        // clic ne fait que déplier/replier le sous-menu. La
                        // navigation se fait en cliquant sur un sub-item.
                        <SidebarMenuButton
                          isActive={isActive}
                          tooltip={item.label}
                          onClick={() => toggleExpanded(item.path, isOpen)}
                        >
                          <Icon />
                          <span>{item.label}</span>
                        </SidebarMenuButton>
                      ) : (
                        // Item feuille : navigation directe vers la page.
                        <SidebarMenuButton
                          asChild
                          isActive={isActive}
                          tooltip={item.label}
                        >
                          <Link to={item.path}>
                            <Icon />
                            <span>{item.label}</span>
                          </Link>
                        </SidebarMenuButton>
                      )}
                      {showPendingBadge && (
                        <SidebarMenuBadge
                          title={badgeTitle}
                          className="bg-red-500 text-white shadow-[0_0_8px_rgba(239,68,68,0.5)] tabular-nums"
                        >
                          {badgeCount}
                        </SidebarMenuBadge>
                      )}
                      {hasAnySubs && !showPendingBadge && !isEditingLayout && (
                        <SidebarMenuAction
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleExpanded(item.path, isOpen);
                          }}
                          aria-label={`${isOpen ? 'Replier' : 'Déplier'} le sous-menu ${item.label}`}
                          title={isOpen ? 'Replier' : 'Déplier'}
                        >
                          <ChevronDown
                            className={cn(
                              'transition-transform duration-200',
                              !isOpen && '-rotate-90'
                            )}
                          />
                        </SidebarMenuAction>
                      )}

                      {/* Mode édition : flèches haut/bas pour réordonner */}
                      {isEditingLayout && isOwner && (
                        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              moveItem(itemIndex, -1);
                            }}
                            disabled={itemIndex === 0 || updateLayoutMutation.isPending}
                            title="Monter"
                            className="h-6 w-6 flex items-center justify-center rounded text-amber-400 hover:bg-amber-500/15 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              moveItem(itemIndex, 1);
                            }}
                            disabled={
                              itemIndex === orderedItems.length - 1 ||
                              updateLayoutMutation.isPending
                            }
                            title="Descendre"
                            className="h-6 w-6 flex items-center justify-center rounded text-amber-400 hover:bg-amber-500/15 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </SidebarMenuItem>

                    {/* Sous-items expansibles (Inventaire / Paie tips / Prix de vente) */}
                    {showSubItems && subItemsForParent && (
                      <SidebarMenuSub>
                        {subItemsForParent
                          .filter((s) => {
                            // Sub-item visible si :
                            //   - aucune restriction (ni admin ni perm) → affiche
                            //   - admin/owner OU perm précise octroyée
                            if (!s.requiresAdmin && !s.permissionId) return true;
                            const adminOk =
                              s.requiresAdmin && isAdminRole(profile?.role);
                            const permOk =
                              s.permissionId &&
                              hasPermission(s.permissionId);
                            return adminOk || permOk;
                          })
                          .map((s) => {
                            const SubIcon = s.icon;
                            const isOrders = s.path === '/inventory/orders';
                            const ordersActive = location.pathname.startsWith('/inventory/orders');

                            // Encadré doré sur le sous-onglet courant : fond +
                            // anneau (ring-inset → aucun décalage de la mise en page).
                            const activeSubClass =
                              'bg-sidebar-accent text-sidebar-accent-foreground font-medium ring-1 ring-inset ring-sidebar-primary/70';

                            // Sub-items avec ?tab= : match manuel sur (basePath, tab).
                            // NavLink ne sait pas matcher sur le query string.
                            const isTabSub = s.tab !== undefined;
                            const tabSubActive = isTabSub
                              ? location.pathname.startsWith(s.path.split('?')[0]) &&
                                currentTab === s.tab
                              : false;

                            return (
                              <Fragment key={s.path}>
                                <SidebarMenuSubItem>
                                  <SidebarMenuSubButton asChild>
                                    {isTabSub ? (
                                      <Link
                                        to={s.path}
                                        className={cn(tabSubActive && activeSubClass)}
                                      >
                                        <SubIcon />
                                        <span>{s.label}</span>
                                      </Link>
                                    ) : (
                                      <NavLink
                                        to={s.path}
                                        end={s.exact}
                                        className={({ isActive: subActive }) =>
                                          cn(subActive && activeSubClass)
                                        }
                                      >
                                        <SubIcon />
                                        <span>{s.label}</span>
                                      </NavLink>
                                    )}
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>

                                {/* Nano-dropdown : responsables sous "Commandes" */}
                                {isOrders && ordersActive && ordersByResp.length > 0 && (
                                  <SidebarMenuSub className="ml-3 border-l border-sidebar-border">
                                    {ordersByResp.map(([respName, count]) => {
                                      const isFiltered = location.search === `?resp=${encodeURIComponent(respName)}`;
                                      return (
                                        <SidebarMenuSubItem key={respName}>
                                          <SidebarMenuSubButton asChild size="sm">
                                            <Link
                                              to={`/inventory/orders?resp=${encodeURIComponent(respName)}`}
                                              className={cn(
                                                'flex items-center justify-between gap-2',
                                                isFiltered && 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                                              )}
                                            >
                                              <span className="truncate">{respName}</span>
                                              <span className="text-[10px] tabular-nums opacity-70">
                                                ({count})
                                              </span>
                                            </Link>
                                          </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                      );
                                    })}
                                  </SidebarMenuSub>
                                )}
                              </Fragment>
                            );
                          })}
                      </SidebarMenuSub>
                    )}

                    {/* Sous-items HR (Équipe / Bibliothèque / Paquets / Formation / Répertoire / Suivi des clés) */}
                    {showHrSubItems && (
                      <SidebarMenuSub>
                        {hrSubItems
                          .filter((s) => {
                            // Admin-only items (ex: Emplois) : visibles si admin/owner
                            if (s.requiresAdmin && !isAdminRole(profile?.role))
                              return false;
                            if (s.requiresAdmin) {
                              // OK : admin/owner, on regarde juste hideIfPermission
                              return !(
                                s.hideIfPermission &&
                                hasPermission(s.hideIfPermission)
                              );
                            }
                            // Items avec permissionId : check perm + hide
                            return (
                              !!s.permissionId &&
                              hasPermission(s.permissionId) &&
                              !(
                                s.hideIfPermission &&
                                hasPermission(s.hideIfPermission)
                              )
                            );
                          })
                          .map((s) => {
                            const SubIcon = s.icon;
                            // tab === undefined → item path-based (ex: /keys)
                            // tab === null      → /employees sans ?tab=
                            // tab === string    → /employees?tab=<valeur>
                            const subActive =
                              s.tab === undefined
                                ? location.pathname.startsWith(s.path)
                                : location.pathname.startsWith('/employees') &&
                                  currentTab === s.tab;

                            // Sous « Équipe » uniquement : on déroule la liste
                            // d'employés (actifs + sélectionné) ; sous chaque
                            // employé sélectionné, on déroule ses 6 sections.
                            const isTeamSub = s.tab === null;
                            const sectionParam =
                              new URLSearchParams(location.search).get('section');
                            const currentEmployeeSection = sectionParam ?? 'info';
                            const showEmployeeList =
                              isTeamSub && subActive && !teamListCollapsed;

                            return (
                              <Fragment key={s.path}>
                                <SidebarMenuSubItem className="relative">
                                  <SidebarMenuSubButton asChild isActive={subActive}>
                                    <Link to={s.path}>
                                      <SubIcon />
                                      <span>{s.label}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                  {s.path === '/repertoire' && unreadSms > 0 && (
                                    <span
                                      title={`${unreadSms} SMS non lu${unreadSms > 1 ? 's' : ''}`}
                                      className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white tabular-nums shadow-[0_0_8px_rgba(239,68,68,0.5)]"
                                    >
                                      {unreadSms}
                                    </span>
                                  )}
                                  {isTeamSub && subActive && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setTeamListCollapsed((c) => !c);
                                      }}
                                      aria-label={
                                        teamListCollapsed
                                          ? 'Déplier la liste d\'employés'
                                          : 'Replier la liste d\'employés'
                                      }
                                      title={
                                        teamListCollapsed ? 'Déplier' : 'Replier'
                                      }
                                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                                    >
                                      <ChevronDown
                                        className={cn(
                                          'h-3.5 w-3.5 transition-transform duration-200',
                                          teamListCollapsed && '-rotate-90'
                                        )}
                                      />
                                    </button>
                                  )}
                                </SidebarMenuSubItem>

                                {showEmployeeList && groupedEmployees.length > 0 && (
                                  <SidebarMenuSub className="ml-3 border-l border-sidebar-border">
                                    {groupedEmployees.map((cat) => {
                                      const catExpanded = expandedCategories.has(
                                        cat.id
                                      );
                                      return (
                                      <Fragment key={cat.id}>
                                        <SidebarMenuSubItem>
                                          <button
                                            type="button"
                                            onClick={() => toggleCategory(cat.id)}
                                            className="w-full flex items-center gap-1.5 px-2 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 hover:text-sidebar-accent-foreground select-none"
                                          >
                                            <ChevronDown
                                              className={cn(
                                                'h-3 w-3 shrink-0 transition-transform duration-200',
                                                !catExpanded && '-rotate-90'
                                              )}
                                            />
                                            <span className="truncate">
                                              {cat.label}
                                            </span>
                                            <span className="ml-auto tabular-nums text-muted-foreground/50">
                                              {cat.employees.length}
                                            </span>
                                          </button>
                                        </SidebarMenuSubItem>
                                        {catExpanded && cat.employees.map((emp) => {
                                      const fullName = `${emp.first_name}${
                                        emp.last_name ? ' ' + emp.last_name : ''
                                      }`;
                                      const isSelected = employeeIdParam === emp.id;
                                      const dotColor =
                                        emp.status === 'active'
                                          ? 'bg-green-500'
                                          : emp.status === 'external'
                                          ? 'bg-blue-500'
                                          : 'bg-neutral-500';

                                      return (
                                        <Fragment key={emp.id}>
                                          <SidebarMenuSubItem>
                                            <SidebarMenuSubButton
                                              asChild
                                              isActive={isSelected}
                                              size="sm"
                                            >
                                              <Link to={`/employees?employee=${emp.id}`}>
                                                <span
                                                  className={cn(
                                                    'h-1.5 w-1.5 rounded-full shrink-0',
                                                    dotColor
                                                  )}
                                                />
                                                <span className="truncate">
                                                  {fullName}
                                                </span>
                                              </Link>
                                            </SidebarMenuSubButton>
                                          </SidebarMenuSubItem>

                                          {/* Sections de l'employé sélectionné */}
                                          {isSelected && (
                                            <SidebarMenuSub className="ml-3 border-l border-sidebar-border">
                                              {employeeSections.map((sec) => {
                                                const SecIcon = sec.icon;
                                                const isCurrentSection =
                                                  currentEmployeeSection === sec.id;
                                                const params = new URLSearchParams();
                                                params.set('employee', emp.id);
                                                if (sec.id !== 'info') {
                                                  params.set('section', sec.id);
                                                }
                                                return (
                                                  <SidebarMenuSubItem key={sec.id}>
                                                    <SidebarMenuSubButton
                                                      asChild
                                                      isActive={isCurrentSection}
                                                      size="sm"
                                                    >
                                                      <Link to={`/employees?${params.toString()}`}>
                                                        <SecIcon />
                                                        <span>{sec.label}</span>
                                                      </Link>
                                                    </SidebarMenuSubButton>
                                                  </SidebarMenuSubItem>
                                                );
                                              })}
                                            </SidebarMenuSub>
                                          )}
                                        </Fragment>
                                      );
                                        })}
                                      </Fragment>
                                      );
                                    })}
                                    {inactiveCount > 0 && (
                                      <SidebarMenuSubItem>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const next = !showInactive;
                                            setShowInactive(next);
                                            if (next) {
                                              setExpandedCategories((prev) =>
                                                new Set(prev).add(INACTIVE_CATEGORY)
                                              );
                                            }
                                          }}
                                          className="w-full flex items-center px-2 pt-2 pb-1 text-[10px] font-medium text-muted-foreground/60 hover:text-sidebar-accent-foreground select-none"
                                        >
                                          {showInactive
                                            ? 'Masquer les anciens'
                                            : `Voir les anciens (${inactiveCount})`}
                                        </button>
                                      </SidebarMenuSubItem>
                                    )}
                                  </SidebarMenuSub>
                                )}
                              </Fragment>
                            );
                          })}
                      </SidebarMenuSub>
                    )}

                    {/* Sous-items Quarts (5 onglets) — basés sur ?tab= */}
                    {showShiftsSubItems && (
                      <SidebarMenuSub>
                        {shiftsSubItems
                          .filter((s) => !!s.permissionId && hasPermission(s.permissionId))
                          .map((s) => {
                            const SubIcon = s.icon;
                            const subActive = currentTab === s.tab;
                            return (
                              <SidebarMenuSubItem key={s.path}>
                                <SidebarMenuSubButton asChild isActive={subActive}>
                                  <Link to={s.path}>
                                    <SubIcon />
                                    <span>{s.label}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            );
                          })}
                      </SidebarMenuSub>
                    )}
                  </Fragment>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* ─── Footer : nom user (lien Mon profil) + Déconnexion ─── */}
      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              size="lg"
              tooltip={
                pendingDocsCount > 0
                  ? `${pendingDocsCount} document${pendingDocsCount > 1 ? 's' : ''} à remplir`
                  : 'Mon profil'
              }
            >
              <Link to="/mon-profil">
                <User className="text-primary" />
                <div className="grid flex-1 text-left leading-tight min-w-0">
                  <span className="text-sm font-medium text-foreground truncate" title={email}>
                    {displayName}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {roleLabel}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
            {pendingDocsCount > 0 && (
              <SidebarMenuBadge
                aria-label={`${pendingDocsCount} document(s) à remplir`}
                className="bg-red-500 text-white shadow-[0_0_8px_rgba(239,68,68,0.5)] tabular-nums"
              >
                {pendingDocsCount}
              </SidebarMenuBadge>
            )}
          </SidebarMenuItem>
          <SidebarMenuItem>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start bg-transparent hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-400 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:justify-center"
              onClick={handleSignOut}
            >
              <LogOut className="h-4 w-4 group-data-[collapsible=icon]:mr-0 mr-2" />
              <span className="group-data-[collapsible=icon]:hidden">Déconnexion</span>
            </Button>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

export default function AppLayout() {
  const location = useLocation();
  const pageLabel = getCurrentPageLabel(location.pathname, location.search);

  return (
    <TooltipProvider delayDuration={300}>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          {/* Header sticky : SidebarTrigger + label de la page courante */}
          <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h2 className="font-display text-base font-semibold text-foreground truncate">
              {pageLabel}
            </h2>
          </header>

          <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
            <Outlet />
          </main>

          {/* Chatbot interne staff (aide à utiliser le système de gestion) */}
          <ChatWidget audience="staff" />
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  );
}
